/* istanbul ignore file */

import * as Yup from "yup";
import { FormattedMessage } from "react-intl";
import { countryHasStates, isValidState } from "utils/regions";
import { formatErrorMessages } from "components/Form";
import { emailRegex } from "utils/emailValidation";
import { passesExtraSSNValidation } from "./utils";
import { makeGooglePlaceInputValidation } from "components/Form/AddressWithPlacesAutocomplete/utils";

export const REQUIRES_EMAIL_VERIFICATION = "Requires verification";
export const INVALID_EMAIL = "Invalid email";

const getKYCSchema = (
  {
    countriesNotRequiringAddressDocuments = [],
    sanctionedCountries = [],
    supportedCountriesInclude = [],
    supportedCountriesExclude = [],
  },
  formatMessage,
  condition
) => {
  /**
   * Define validators for schema
   */

  const FormattedErrorMessages = formatErrorMessages(formatMessage);

  const taxUpdate = condition === "TAX_UPDATE";

  const RequiredEmail = Yup.string()
    .email(() => formatMessage({ defaultMessage: "Invalid email", description: "Error message" }))
    .matches(emailRegex)
    .required(FormattedErrorMessages.Required);

  const googlePlaceValidation = makeGooglePlaceInputValidation("street", FormattedErrorMessages);

  const RequiredString = Yup.string().required(FormattedErrorMessages.Required);
  const RequiredAndDefinedString = Yup.string()
    .defined(FormattedErrorMessages.Required)
    .required(FormattedErrorMessages.Required);
  const RequiredStringMinMax = Yup.string()
    .min(2, FormattedErrorMessages.StringMin)
    .max(50, FormattedErrorMessages.StringMax)
    .required(FormattedErrorMessages.Required);
  const StringMinMax = Yup.string()
    .min(2, FormattedErrorMessages.StringMin)
    .max(50, FormattedErrorMessages.StringMax);
  const DefaultStateValidation = Yup.string().max(50, FormattedErrorMessages.StringMax);
  const ExtraFilesValidation = Yup.array()
    .of(Yup.mixed())
    .required(FormattedErrorMessages.AtLeastOneSupportingDocument)
    .min(1, FormattedErrorMessages.AtLeastOneSupportingDocument);

  const MailingAddress = Yup.object({
    street: RequiredStringMinMax,
    street2: Yup.string(),
    city: RequiredStringMinMax,
    zip: RequiredStringMinMax,
    state: DefaultStateValidation.when("country", (country, schema) => {
      if (countryHasStates(country)) {
        return DefaultStateValidation.required(FormattedErrorMessages.Required).test(
          "valid-state-for-country",
          FormattedErrorMessages.InvalidStateForSelectedCountry,
          (state) => isValidState(country, state)
        );
      }
      return schema;
    }),
    country: RequiredString,
    googlePlacesAutocomplete: googlePlaceValidation,
  }).when("useForMailingAddress", (useForMailingAddress, schema) => {
    if (useForMailingAddress) {
      return schema.shape({
        street: StringMinMax,
        street2: Yup.string(),
        city: StringMinMax,
        zip: StringMinMax,
        state: DefaultStateValidation,
        country: Yup.string(),
        googlePlacesAutocomplete: Yup.string(),
      });
    }

    return schema;
  });

  const requiresAdditionalDocuments = (
    country,
    schema,
    countriesNotRequiringAddressDocuments,
    pii_level,
    condition
  ) => {
    let out = schema;
    let shape = {};

    // if pii_level is 'basic', then we check for other conditions:
    // all countries except for US require additional documents
    // US only requires additional documents if it requires proof of address additional documents
    // if US does not require proof of address documents, then it does not require any additional documents
    const isRequiredAdditionalDocuments =
      condition !== "TAX_UPDATE" &&
      pii_level !== "basic" &&
      (country !== "US" ||
        (country === "US" && !countriesNotRequiringAddressDocuments.includes("US")));

    if (isRequiredAdditionalDocuments) {
      shape = {
        document_type: RequiredAndDefinedString,
        document_front: Yup.mixed()
          .defined(FormattedErrorMessages.Required)
          .required(FormattedErrorMessages.Required),
        document_back: Yup.mixed().when("document_type", {
          is: "PP",
          otherwise: Yup.mixed()
            .defined(FormattedErrorMessages.Required)
            .required(FormattedErrorMessages.Required),
        }),
        document_country: RequiredAndDefinedString,
      };

      if (!countriesNotRequiringAddressDocuments.includes(country)) {
        // Countries requiring proof of address documents get asked
        // "Is your current address on this document?" and if not, the need
        // to provide supporting documentation.
        shape = {
          ...shape,
          is_address_on_document: RequiredAndDefinedString,
          extra_files: Yup.array().when(["is_address_on_document"], {
            is: (is_address_on_document) => is_address_on_document !== "yes",
            then: ExtraFilesValidation,
          }),
        };
      }
    }

    out = out.shape(shape);

    return out;
  };

  /**
   * Schema output
   */

  return Yup.object({
    email: RequiredEmail,
    country: Yup.string()
      .required(FormattedErrorMessages.Required)
      .test(
        "unsupportedCountry",
        () =>
          formatMessage({
            defaultMessage: "Your country is not eligible for this feature",
            description: "Error message",
          }),
        (value) => {
          // sanction country check comes first and overrides everything else
          // next, we check if value is in an integrator exclude list
          if (sanctionedCountries.includes(value) || supportedCountriesExclude.includes(value)) {
            return false;
          }
          // finally, if the integrator include list contains at least one value we reject everything that is not in the list
          if (supportedCountriesInclude.length > 0 && !supportedCountriesInclude.includes(value)) {
            return false;
          }
          // otherwise country value is good to go
          return true;
        }
      ),
    personal: Yup.object({
      first: RequiredStringMinMax,
      middle: Yup.string(),
      last: RequiredStringMinMax,
      dob: taxUpdate
        ? null
        : Yup.object()
            .shape({
              day: RequiredString,
              month: RequiredString,
              year: RequiredString,
            })
            .test(
              "minAge",
              {
                minAge: (
                  <FormattedMessage
                    defaultMessage="Users under 18 are not eligible for this feature"
                    description="Error message"
                  />
                ),
              },
              ({ day, month, year }) => {
                let isAbove18 = false;
                if (
                  day &&
                  parseInt(day, 10) &&
                  month &&
                  parseInt(month, 10) &&
                  year &&
                  parseInt(year, 10)
                ) {
                  const birthDate = new Date(
                    parseInt(year, 10),
                    parseInt(month, 10) - 1,
                    parseInt(day, 10)
                  ); // since the value for month coming in is not index based we have to subtract 1
                  const today = new Date();
                  let age = today.getFullYear() - birthDate.getFullYear();
                  const m = today.getMonth() - birthDate.getMonth();
                  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                  }
                  isAbove18 = age >= 18;
                }
                return isAbove18;
              }
            ),
    }),

    taxInfo: Yup.object({
      signature: RequiredStringMinMax,
    }).when("country", (country, schema) => {
      let out = schema;
      if (country === "US") {
        out = out.shape({
          ssn: taxUpdate
            ? null
            : Yup.string()
                .matches(/^(\d{3}-?\d{2}-?\d{4}|XXX-XX-XXXX)$/, FormattedErrorMessages.InvalidSsn)
                .test(
                  "ssn-xtra-checks",
                  FormattedErrorMessages.InvalidSsn,
                  passesExtraSSNValidation
                )
                .required(FormattedErrorMessages.Required),
        });
      }
      return out;
    }),

    contact: Yup.object({
      street: RequiredStringMinMax,
      street2: Yup.string(),
      city: RequiredStringMinMax,
      zip: RequiredStringMinMax,
      state: DefaultStateValidation,
      country: RequiredString,
      googlePlacesAutocomplete: googlePlaceValidation,
      useForMailingAddress: Yup.boolean().required(),
      mailingAddress: MailingAddress,
    }).when("country", (country, schema) => {
      let out = schema;
      if (countryHasStates(country)) {
        out = out.shape({
          state: DefaultStateValidation.required(FormattedErrorMessages.Required).test(
            "valid-state-for-country",
            FormattedErrorMessages.InvalidStateForSelectedCountry,
            (state) => isValidState(country, state)
          ),
        });
      }
      return out;
    }),

    documents: Yup.object().when(
      ["country", "contact.state", "pii_level"],
      (country, _state, pii_level, schema) =>
        requiresAdditionalDocuments(
          country,
          schema,
          countriesNotRequiringAddressDocuments,
          pii_level,
          condition
        )
    ),

    __metadata: Yup.object({
      permanentGooglePlaceFormattedAddress: Yup.string(),
      mailingGooglePlaceFormattedAddress: Yup.string(),
    }),
  });
};

export default getKYCSchema;